import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { ChevronRight } from 'lucide-react';

import { homeTiles, tileDate } from './homeTiles';
import {
  Card,
  MetricBreathIcon,
  MetricFatigueIcon,
  MetricFormantIcon,
  MetricPitchIcon,
  MetricRangeIcon,
  MetricResonanceIcon,
  MetricVibratoIcon,
  SectionHeadCard,
  TransitionLink,
} from '../../components';

import type { DiagnosisRecord } from '../onboarding/record';
import type { MetricTabId } from '../onboarding/resultCopy';

/**
 * 지표 타일 일곱(시안 §7.1) — 마지막 회차의 한눈 요약이자 상세로 가는 문.
 *
 * **타일과 상세 탭이 같은 목록이다.** 이름도 순서도 `ResultTabs`와 같은 것을 쓰고, 누르면
 * 그 탭으로 간다(`/result/:id/:tab`). 홈에서 셋째로 본 지표가 상세에서 다섯째면 같은
 * 목록이라는 걸 못 읽는다.
 *
 * 배치는 시안대로 **4 + 3** — 첫 줄 넷, 둘째 줄 셋, 간격 6px. 타일은 흰색·라운드 12·
 * 그림자, 라벨 11px(BK_600) 위에 값 15px SemiBold, 우하단에 32px 회색 원 안 지표 아이콘.
 *
 * 헤더는 카드 한 장(`최근 진단 결과` + `2026. 00. 00 >`) — 날짜는 그 회차의 날짜이자
 * 이력으로 가는 링크다.
 *
 * 시안은 못 재는 자리를 `00`으로 그려 뒀지만 그건 자리표시자다 — 여기서는 `측정 준비 중`을
 * 쓴다. 0을 쓰면 화면이 재지도 않은 0점을 말한다.
 */

/**
 * 타일 우하단 아이콘 — 시안 `1014:9946`에서 내보낸 7종. 색이 뜻을 실어(레드/오렌지) 그대로
 * 둔다(`make-icon-components.py`가 `currentColor`로 안 바꾼 이유).
 */
export const METRIC_ICON: Record<
  MetricTabId,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  fatigue: MetricFatigueIcon,
  registers: MetricPitchIcon,
  range: MetricRangeIcon,
  vibrato: MetricVibratoIcon,
  breath: MetricBreathIcon,
  formant: MetricFormantIcon,
  resonance: MetricResonanceIcon,
};

export const MetricTiles = ({ record }: { record: DiagnosisRecord }) => {
  const { t } = useTranslation();
  const tiles = homeTiles(record);
  const date = tileDate(record.completedAt);

  return (
    <section className="mt-8">
      {/* 오른쪽 부속은 **가는 곳을 말한다.** 예전에는 회차 날짜 + 쉐브론이라 눈으로는
          "이 회차 열기"로 읽히는데 실제로는 `/history` 목록으로 갔다(시안 20260730
          §7.2 는 이미 `결과 전체 보기 >` 로 바꿔 놨다). 날짜는 정보라 지우지 않고
          제목 아래 캡션으로 내린다 — 누를 수 없는 글자면 약속을 하지 않는다. */}
      <SectionHeadCard
        title={
          <>
            {t('web.home.tiles.section')}
            <span className="mt-0.5 block text-[13px] font-normal tracking-[-0.065px] text-bk-600">
              {t(date.key, date.params)}
            </span>
          </>
        }
        right={
          <TransitionLink
            to="/history"
            className="flex min-h-[44px] items-center gap-0.5 text-[15px] tracking-[-0.075px] text-bk-600"
          >
            {t('web.home.recent.all')}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </TransitionLink>
        }
      />

      <ul
        aria-label={t('web.home.tiles.section')}
        className="mt-6 grid grid-cols-12 gap-1.5"
      >
        {tiles.map(({ tab, value, valueKey, valueParams }, i) => {
          // 못 잰 타일 = 숫자도 문장 키도 없다. 빈 문자열 같은 자리표시자를 두지 않는다.
          const measured = value !== null || valueKey !== undefined;
          const Icon = METRIC_ICON[tab];
          /**
           * 못 잰 타일은 **누를 수 없다.** 링크로 두면 `측정 준비 중`을 눌러 똑같이
           * `측정 준비 중`만 있는 탭으로 간다 — 일곱 중 넷이 그 길이었다(2026-09-16
           * 실기기). 타일 자체는 남긴다: 사라지면 그런 축이 있다는 사실이 없어진다.
           */
          const body = (
            <Card className="flex h-full flex-col gap-2 rounded-xl px-2 py-3">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] leading-tight tracking-[-0.055px] text-bk-600">
                  {t(`web.result.tabs.name.${tab}`)}
                </span>
                {/* 타일이 좁다(4열 ≈ 78px). 값은 한 줄에 — `E2-C5`가 하이픈에서 꺾이면 음역이
                        두 줄로 갈라져 읽힌다. */}
                <span
                  className={
                    measured
                      ? 'whitespace-nowrap text-[15px] font-semibold tracking-[-0.075px] tabular-nums text-[#12131a]'
                      : 'text-[10px] font-medium leading-tight text-bk-500'
                  }
                >
                  {valueKey
                    ? t(valueKey, valueParams)
                    : (value ?? t('web.result.pending'))}
                </span>
              </div>
              <span
                aria-hidden="true"
                className="ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-bk-50"
              >
                <Icon className="h-[18px] w-[18px]" />
              </span>
            </Card>
          );
          return (
            <li key={tab} className={cn(i < 4 ? 'col-span-3' : 'col-span-4')}>
              {measured ? (
                <TransitionLink
                  to={`/result/${record.id}/${tab}`}
                  className="block h-full"
                >
                  {body}
                </TransitionLink>
              ) : (
                <div className="block h-full">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};
